import { authorizedTokenFromJSON } from "common/records/AuthorizedToken";
import { AuthorizedTokensRecord, authorizedTokensFromJSON } from "common/records/AuthorizedTokens";
import { CrmFilter, TokenKind, TokenRecord, tokenFromJSON } from "common/records/Token";
import { Record, Map, List, fromJS } from "immutable";
import { RootActions, RootState } from "common/reducers";
import {
  ADD_ACTIVE_MANUFACTURER,
  ADD_DATA_ITEM,
  ADD_FILTER,
  ADD_FILTER_VALUE,
  ADD_IMAGE_ID,
  CREATE_AVAILABLE_MANUFACTURER,
  EDIT_TOKEN,
  FETCH_AUTHORIZED,
  FETCH_TOKEN,
  ON_SORT_END,
  RECEIVE_AUTHORIZED,
  RECEIVE_TOKEN,
  RECEIVE_TOKENS,
  REMOVE_ACTIVE_MANUFACTURER,
  REMOVE_AVAILABLE_MANUFACTURER,
  REMOVE_DATA_ITEM,
  REMOVE_FILTER,
  REMOVE_FILTER_VALUE,
  REMOVE_TOKEN,
  RESET_TOKEN,
  SET_AUTHORIZED_LOADED,
  SET_LOADED,
  TokenActions,
} from "common/reducers/token.actions";
import { activeManufacturers, availableManufacturers } from "common/selectors/tokenFeneTech.selectors";
import { feneTechManufacturerFromJSON, FeneTechManufacturerRecord } from "common/records/FeneTechManufacturer";
import { reducer as imageReducer } from "common/reducers/image.reducer";
import { ImageActions } from "common/reducers/image.actions";
import { tokenImageIds } from "common/selectors/token.selectors";

export interface ITokenStateRecord {
  authByOrgId: Map<number, AuthorizedTokensRecord>;
  byOrgId: Map<number, Map<TokenKind, TokenRecord>>;
  lastSavedByOrgId: Map<number, Map<TokenKind, TokenRecord>>;
}

export const defaultTokenStateRecord: ITokenStateRecord = {
  authByOrgId: Map<number, AuthorizedTokensRecord>(),
  byOrgId: Map<number, Map<TokenKind, TokenRecord>>(),
  lastSavedByOrgId: Map<number, Map<TokenKind, TokenRecord>>(),
};

export class TokenStateRecord extends Record<ITokenStateRecord>(defaultTokenStateRecord) implements ITokenStateRecord {}

export const initialState = new TokenStateRecord();

const modelName = "tokens";
export const reducer = (state: RootState, action: RootActions): RootState => {
  switch (action.type) {
    case FETCH_TOKEN: {
      const { orgId, tokenKind } = action.payload;
      if (state.getIn([modelName, "byOrgId", orgId, tokenKind])) {
        return state.setIn([modelName, "byOrgId", orgId, tokenKind, "loading"], true);
      }
      return state.setIn([modelName, "byOrgId", orgId, tokenKind], tokenFromJSON({ kind: tokenKind, loading: true }));
    }

    case RECEIVE_TOKEN: {
      const { orgId, token } = action.payload;
      const tokenRecord = tokenFromJSON(token);
      state = state.setIn(
        [modelName, "authByOrgId", orgId, "byKind", token.kind],
        authorizedTokenFromJSON({ authorized: token.valid_token, id: token.id }),
      );
      token?.images?.forEach((image) => {
        state = imageReducer(state, ImageActions.receiveImage(image));
      });

      return state
        .setIn([modelName, "byOrgId", orgId, token.kind], tokenRecord)
        .setIn([modelName, "lastSavedByOrgId", orgId, token.kind], tokenRecord);
    }

    case RECEIVE_TOKENS: {
      const { orgId, tokens } = action.payload;

      tokens.forEach((token: any) => {
        state = reducer(state, TokenActions.receiveToken(orgId, token));
      });

      return state;
    }

    case RESET_TOKEN: {
      const { orgId, tokenKind } = action.payload;
      return state.setIn(
        [modelName, "byOrgId", orgId, tokenKind],
        state.getIn([modelName, "lastSavedByOrgId", orgId, tokenKind]),
      );
    }

    case REMOVE_TOKEN: {
      const { orgId, tokenKind } = action.payload;
      const tokenRecord = tokenFromJSON({ org_id: orgId, kind: tokenKind });
      return state
        .setIn([modelName, "byOrgId", orgId, tokenKind], tokenRecord)
        .setIn([modelName, "lastSavedByOrgId", orgId, tokenKind], tokenRecord)
        .setIn([modelName, "authByOrgId", orgId, "byKind", tokenKind], authorizedTokenFromJSON({}));
    }

    case ADD_FILTER: {
      const { orgId, tokenKind } = action.payload;
      const list = state.getIn([modelName, "byOrgId", orgId, tokenKind, "data", "filters"], List([])) as List<any>;
      const emptyFilter = Map({ column: "", valid_values: List([""]) }) as CrmFilter;
      const newList = list.insert(list.size, emptyFilter);
      return state.setIn([modelName, "byOrgId", orgId, tokenKind, "data", "filters"], newList);
    }

    case REMOVE_FILTER: {
      const { orgId, tokenKind, filterIndex } = action.payload;
      const list = state.getIn([modelName, "byOrgId", orgId, tokenKind, "data", "filters"], List([])) as List<any>;
      const newList = list.delete(filterIndex);
      return state.setIn([modelName, "byOrgId", orgId, tokenKind, "data", "filters"], newList);
    }

    case ADD_FILTER_VALUE: {
      const { orgId, tokenKind, filterIndex, valueIndex } = action.payload;
      const list = state.getIn(
        [modelName, "byOrgId", orgId, tokenKind, "data", "filters", filterIndex, "valid_values"],
        List([]),
      ) as List<any>;
      const newList = list.insert(valueIndex + 1, "");
      return state.setIn(
        [modelName, "byOrgId", orgId, tokenKind, "data", "filters", filterIndex, "valid_values"],
        newList,
      );
    }

    case REMOVE_FILTER_VALUE: {
      const { orgId, tokenKind, filterIndex, valueIndex } = action.payload;
      const list = state.getIn(
        [modelName, "byOrgId", orgId, tokenKind, "data", "filters", filterIndex, "valid_values"],
        List<any>([]),
      ) as List<any>;
      const newList = list.delete(valueIndex);
      return state.setIn(
        [modelName, "byOrgId", orgId, tokenKind, "data", "filters", filterIndex, "valid_values"],
        newList,
      );
    }

    case SET_LOADED: {
      const { orgId, tokenKind } = action.payload;
      state = state.setIn([modelName, "byOrgId", orgId, tokenKind, "loading"], false);
      return state.setIn([modelName, "lastSavedByOrgId", orgId, tokenKind, "loading"], false);
    }

    case FETCH_AUTHORIZED:
      if (state.getIn([modelName, "authByOrgId", action.payload])) {
        return state.setIn([modelName, "authByOrgId", action.payload, "loading"], true);
      }
      return state
        .setIn([modelName, "authByOrgId", action.payload], authorizedTokensFromJSON({}))
        .setIn([modelName, "authByOrgId", action.payload, "loading"], true);

    case RECEIVE_AUTHORIZED: {
      const { orgId, authorized } = action.payload;
      Object.keys(authorized).forEach((key) => {
        state = state.setIn(
          [modelName, "authByOrgId", orgId, "byKind", key],
          authorizedTokenFromJSON(authorized[key as TokenKind]),
        );
      });
      return state.setIn([modelName, "authByOrgId", orgId, "loading"], false);
    }

    case SET_AUTHORIZED_LOADED:
      return state.setIn([modelName, "authByOrgId", action.payload, "loading"], false);

    case ADD_DATA_ITEM: {
      const { orgId, tokenKind, itemName, newItem } = action.payload;
      const list = state.getIn([modelName, "byOrgId", orgId, tokenKind, "data", itemName], List([])) as List<any>;
      const newList = list.insert(list.size, fromJS(newItem));
      return state.setIn([modelName, "byOrgId", orgId, tokenKind, "data", itemName], newList);
    }

    case REMOVE_DATA_ITEM: {
      const { orgId, tokenKind, itemName, index } = action.payload;
      const list = state.getIn([modelName, "byOrgId", orgId, tokenKind, "data", itemName], List([])) as List<any>;
      const newList = list.delete(index);
      return state.setIn([modelName, "byOrgId", orgId, tokenKind, "data", itemName], newList);
    }

    case ADD_ACTIVE_MANUFACTURER: {
      const { orgId, manufacturerId } = action.payload;
      const available = availableManufacturers(state);
      const index = available.findIndex((m) => m.id === manufacturerId);
      const moving = available.get(index) as FeneTechManufacturerRecord;

      const editedActive = activeManufacturers(state).push(moving);
      const editedAvailable = available.delete(index);

      return state
        .setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "availableManufacturers"], editedAvailable)
        .setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "manufacturers"], editedActive);
    }

    case REMOVE_ACTIVE_MANUFACTURER: {
      const { orgId, manufacturerId } = action.payload;
      const active = activeManufacturers(state);
      const index = active.findIndex((m) => m.id === manufacturerId);
      const moving = active.get(index) as FeneTechManufacturerRecord;

      const editedActive = active.delete(index);
      const editedAvailable = availableManufacturers(state).push(moving);

      return state
        .setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "availableManufacturers"], editedAvailable)
        .setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "manufacturers"], editedActive);
    }

    case CREATE_AVAILABLE_MANUFACTURER: {
      const { orgId, manufacturerId } = action.payload;
      const newManufacturer = feneTechManufacturerFromJSON({ id: manufacturerId });

      const editedAvailable = availableManufacturers(state).push(newManufacturer);

      return state.setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "availableManufacturers"], editedAvailable);
    }

    case REMOVE_AVAILABLE_MANUFACTURER: {
      const { orgId, manufacturerId } = action.payload;
      const available = availableManufacturers(state);
      const index = available.findIndex((m) => m.id === manufacturerId);

      const editedAvailable = available.delete(index);

      return state.setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "availableManufacturers"], editedAvailable);
    }

    case ON_SORT_END: {
      const { orgId, oldIndex, newIndex } = action.payload;
      const list = activeManufacturers(state);
      const moving = list.get(oldIndex) as FeneTechManufacturerRecord;

      const sorted = list.delete(oldIndex).insert(newIndex, moving);

      return state.setIn([modelName, "byOrgId", orgId, "fene_tech", "data", "manufacturers"], sorted);
    }

    case EDIT_TOKEN: {
      const { orgId, tokenKind, path, value } = action.payload;
      return state.setIn([modelName, "byOrgId", orgId, tokenKind].concat(path), value);
    }

    case ADD_IMAGE_ID: {
      const { orgId, tokenKind, imageId } = action.payload;
      const ids = tokenImageIds(state, { kind: tokenKind }).push(imageId);
      return state.setIn([modelName, "byOrgId", orgId, tokenKind, "image_ids"], ids);
    }

    default:
      return state;
  }
};
